import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';
import { Box, Divider, Paper, Typography, Unstable_Grid2 as Grid } from '@mui/material';

import { CompanyDetails, GetEbrOrganicInsightsQuery } from 'api';
import { SocialAvatar } from 'components';

import { Bars } from '../Components';
import { common, COLORS } from '../styles';

interface Props {
  queryOptions: UseQueryResult<GetEbrOrganicInsightsQuery>;
}

const styles = {
  divider: {
    borderColor: 'white',
    marginTop: 5,
    marginBottom: 4,
  },
  header: {
    ...common.header,
    marginBottom: 2,
  },
  subheader: {
    ...common.subheader,
    marginTop: 4,
    marginBottom: 2,
  },
  subheader2: {
    ...common.subheader,
    marginTop: 8,
    marginBottom: 2,
  },
  paper: {
    border: `1px solid ${COLORS.gray2}`,
    borderRadius: 12,
    backgroundColor: 'transparent',
    padding: 3,
  },
  keyPoints: {
    ...common.subheader,
    fontWeight: 800,
    marginTop: 8,
  },
};

const AdvocacyOrganicInsights: FC<Props> = ({ queryOptions }) => {
  const { t } = useTranslation();

  const { data } = queryOptions;
  const {
    key_insights = '',
    top_companies = [],
    countries = [],
    company_industries = [],
    company_sizes = [],
    job_titles = [],
    job_roles = [],
  } = data?.getEbrOrganicInsights ?? {};

  function renderCompany(company: CompanyDetails, index: number) {
    return (
      <Grid key={index}>
        <SocialAvatar
          variant="square"
          name={company.job_company_name}
          key={index}
          image={company.company_logo}
          size={90}
          style={{ borderRadius: 4 }}
        />
      </Grid>
    );
  }

  return (
    <Fragment>
      <Typography sx={styles.header}>{t('advocacy:organicInsights')}</Typography>
      <Typography sx={common.paragraph}>{key_insights}</Typography>
      <Typography sx={styles.subheader}>{t('advocacy:organicInsightsSubheader')}</Typography>
      <Grid alignItems="center" container spacing={2}>
        {top_companies.map(renderCompany)}
      </Grid>
      <Typography sx={styles.subheader2}>{t('advocacy:organicInsightsSubheader2')}</Typography>
      <Grid container spacing={4}>
        <Grid xs={6}>
          <Paper sx={styles.paper}>
            <Typography marginBottom={2} sx={common.paragraph}>
              {t('advocacy:companyCountries')}
            </Typography>
            <Bars data={countries} />
          </Paper>
        </Grid>
        <Grid xs={6}>
          <Paper sx={styles.paper}>
            <Typography marginBottom={2} sx={common.paragraph}>
              {t('advocacy:companyTitles')}
            </Typography>
            <Bars data={job_titles} />
          </Paper>
        </Grid>
        <Grid xs={6}>
          <Paper sx={styles.paper}>
            <Typography marginBottom={2} sx={common.paragraph}>
              {t('advocacy:companyIndustries')}
            </Typography>
            <Bars data={company_industries} />
          </Paper>
        </Grid>
        <Grid xs={6}>
          <Paper sx={styles.paper}>
            <Typography marginBottom={2} sx={common.paragraph}>
              {t('advocacy:companyRoles')}
            </Typography>
            <Bars data={job_roles} />
          </Paper>
        </Grid>
        <Grid xs={6}>
          <Paper sx={styles.paper}>
            <Typography marginBottom={2} sx={common.paragraph}>
              {t('advocacy:companySize')}
            </Typography>
            <Bars data={company_sizes} />
          </Paper>
        </Grid>
      </Grid>
      <Typography sx={styles.keyPoints}>{t('advocacy:keyPoints')}</Typography>
      <Divider sx={styles.divider} />
      <Box marginBottom={8}>
        <Typography sx={common.paragraph}>{t('advocacy:organicInsightsText')}</Typography>
      </Box>
    </Fragment>
  );
};

export default AdvocacyOrganicInsights;
